/**
 * A Google consent popup opened by a tab loaded before item 66 (item 88b).
 *
 * That tab speaks the old protocol: it never finishes the consent (the
 * callback used to), it waits on the channel below for the message below
 * naming the level it asked for, and it polls the person's levels meanwhile.
 * Nothing of the new protocol answers it, so the landing page finishes such a
 * consent itself — only with the popup's own session, which is a copy of the
 * opener's (utils/oauthPopup.ts `finishOAuthHere`) — and then tells that tab
 * in its own words, so its wait ends now and a "No" or another account
 * reaches it, instead of a five-minute spinner. The old tab trusts only the
 * server's levels for a grant (it reads them again): the message decides
 * nothing.
 */

import { finishOAuthHere, type OAuthCallback, type OAuthOutcome } from '@/utils/oauthPopup';

export const LEGACY_ACCESS_CHANNEL = 'corpbrain-google-access';
export const LEGACY_ACCESS_MESSAGE = 'corpbrain:google-access';

/** The statuses the old tab knew; anything else it reads as an error. */
const LEGACY_STATUS: Record<string, string> = {
  granted: 'granted',
  declined: 'declined',
  wrong_account: 'wrong_account',
};

export function legacyAccessMessage(outcome: string, access: string) {
  return { type: LEGACY_ACCESS_MESSAGE, status: LEGACY_STATUS[outcome] ?? 'error', access };
}

/** Say it as the old landing page did: on the channel, and to the opener while there is one. */
export function tellLegacyOpener(outcome: string, access: string): void {
  const message = legacyAccessMessage(outcome, access);
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(LEGACY_ACCESS_CHANNEL);
    channel.postMessage(message);
    channel.close();
  }
  try { window.opener?.postMessage(message, window.location.origin); } catch { /* severed by COOP */ }
}

/**
 * The landing page's `finishHere` for Google: finish the consent no window
 * took as this popup's own person, and tell an old tab how it ended. Null —
 * nothing was granted, as before — when this page has no session, its
 * session is over, or the state is not this person's.
 */
export async function finishConsentHere(finishUrl: string, callback: OAuthCallback): Promise<OAuthOutcome | null> {
  const answer = await finishOAuthHere(finishUrl, callback);
  if (!answer || answer.outcome === 'invalid_state') return null;
  // A first connection made in a popup reads as granted, as it always did.
  const outcome = answer.outcome === 'connected' ? 'granted' : answer.outcome;
  if (answer.access) tellLegacyOpener(outcome, answer.access);
  return { outcome };
}
